import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { BOTTOM_NAV_HEIGHT, BottomNav, HeaderIcons, SideNav } from './nav.js';
import { ErrorBoundary } from '../components/error-boundary.js';
import { Button } from '../components/button.js';
import { useLogout, useSession } from '../data/session.js';
import { cx } from '../lib/cx.js';
import { Icon } from '../components/icon.js';
import { Wordmark } from '../components/wordmark.js';

/**
 * The frame every signed-in screen sits in.
 *
 * The bottom padding on mobile is not decoration: without it the bottom
 * navigation covers the last row of every list on a phone. It follows
 * `--aw-nav-h`, which each navigation sets, rather than a fixed value that
 * is right for exactly one of them.
 */
export function AppLayout() {
  const { pathname } = useLocation();

  return (
    // The bar's height is set here rather than on the bar, because the
    // content pads itself by it and the two are siblings.
    <div
      className="flex min-h-dvh bg-ground"
      style={{ ['--aw-nav-h' as string]: BOTTOM_NAV_HEIGHT }}
    >
      {/* The target already existed; nothing pointed at it. Keyboard and
          switch users had to walk the whole rail on every navigation. */}
      <a
        href="#main"
        className="sr-only z-50 rounded-aw bg-ink px-4 py-2 text-sm font-semibold text-on-accent focus:not-sr-only focus:absolute focus:top-2 focus:left-2"
      >
        Skip to content
      </a>

      <SideNav />

      <div className="flex min-w-0 flex-1 flex-col">
        <OfflineBanner />
        <MobileHeader />

        {/* Keyed on the path so a screen that throws is discarded when the
            owner navigates away. Without the key the boundary instance
            outlives the route — one crash and every destination in the rail
            kept rendering the same error panel, which read as dead
            navigation rather than as a broken screen. */}
        {/* `grid` so whatever is inside gets the real remaining height.
            The ancestors are sized with `min-h-dvh`, which is a minimum and
            not a definite height, so `h-full` on a child resolves to auto and
            anything trying to centre itself vertically has nothing to centre
            against — which is why the loading animation sat a fifth of the
            way down. A single grid item stretches to the row, and the row is
            the space that is actually left. */}
        <main
          id="main"
          tabIndex={-1}
          className="grid min-w-0 flex-1 pb-[calc(var(--aw-nav-h,64px)+env(safe-area-inset-bottom))] outline-none md:pb-0"
        >
          <RouteChange pathname={pathname} />
          <ErrorBoundary key={pathname}>
            <Suspense fallback={<ScreenSkeleton />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <BottomNav />
    </div>
  );
}

/**
 * A data router changes the URL without doing either of the things a browser
 * does on a real navigation: it does not reset the scroll position, and it
 * does not move focus. So arriving at Posts from halfway down Settings landed
 * mid-list, and a screen reader stayed parked wherever the last link was.
 *
 * The first paint is skipped deliberately — scrolling to the top on mount
 * would fight the browser's own restoration on a reload or a back button.
 */
function RouteChange({ pathname }: { pathname: string }) {
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.getElementById('main')?.focus({ preventScroll: true });
  }, [pathname]);

  return null;
}

/**
 * The phone header. The rail already carries the name on desktop, so this
 * exists mainly to reach Media, Settings and sign-out without stealing one of
 * the five thumb-reachable slots.
 */
function MobileHeader() {
  const [open, setOpen] = useState(false);
  const session = useSession();
  const logout = useLogout();
  const { pathname } = useLocation();

  // Closing happens on the tap that navigates rather than in an effect
  // watching the path: same result, one render instead of a cascade.
  const close = () => {
    setOpen(false);
  };

  // ...but a tap is not the only way to leave. The bottom bar, the skip link,
  // the browser's back button and any in-screen link all changed the route
  // with the menu still hanging open over the new screen.
  //
  // Adjusted during render rather than in an effect, which is both what the
  // composer does for the same class of problem and what the lint rule
  // insists on: a setState inside an effect costs an extra render and a
  // visible frame of the menu sitting over the screen it no longer belongs to.
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    if (open) setOpen(false);
  }

  // Escape and a tap outside are what every other dismissible surface in the
  // app honours (components/sheet.tsx); this one honoured neither, so on a
  // phone the only way out was to find the More button again.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && !target.closest('header')) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-surface pt-[env(safe-area-inset-top)] md:hidden">
      <div className="flex min-h-[52px] items-center justify-between gap-2 px-3">
        <Link to="/" onClick={close} aria-label="Dintiko home">
          <Wordmark size={20} />
        </Link>

        {/* The three that used to hide behind "More", as themselves. What is
            left behind the button is the account — which is not a
            destination, so it keeps a button of its own. */}
        <div className="flex items-center gap-0.5">
          <HeaderIcons />
          <button
            type="button"
            aria-expanded={open}
            aria-controls="account-menu"
            aria-label="Account"
            onClick={() => {
              setOpen((value) => !value);
            }}
            className={cx(
              'grid h-11 w-11 place-items-center rounded-aw transition-colors',
              open ? 'bg-raise text-ink' : 'text-muted',
            )}
          >
            <Icon name="accounts" size={20} />
          </button>
        </div>
      </div>

      {open && (
        <div id="account-menu" className="border-t border-line px-3 py-2">
          {/* Who you are signed in as, and how to stop being. The three
              destinations that used to be listed here are in the header
              beside the button now, so this holds only what it is for. */}
          <div className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate font-mono text-xs text-faint">
              {session.data?.email ?? ''}
            </span>
            <Button
              size="sm"
              variant="quiet"
              busy={logout.isPending}
              onClick={() => {
                logout.mutate();
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

/**
 * Publishing is a server job, so losing the connection does not lose the work
 * — but the owner should know why the screen stopped updating (PLAN §13.3).
 */
function OfflineBanner() {
  const [offline, setOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const goOffline = () => {
      setOffline(true);
    };
    const goOnline = () => {
      setOffline(false);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div role="status" className="bg-warn-soft px-3 py-2 text-center text-sm text-warn">
      You are offline. Scheduled posts still publish from the server.
    </div>
  );
}

/**
 * Four squares pulsing in a ring — the app's mark at its smallest.
 *
 * `bg-ink` rather than a literal black: ink is black on the light theme's
 * paper and white on the dark one's black, so the squares stay visible either
 * way. A hardcoded black would disappear entirely on the dark theme, which is
 * also the background the installed app opens on.
 */
export function PulseSquares({ className }: { className?: string }) {
  // inline-grid, not grid: a block grid stretches to its container and the two
  // columns become half the screen each, which parks the squares in opposite
  // corners instead of in a 2×2 the size of a thumbnail.
  return (
    <span aria-hidden="true" className={cx('inline-grid grid-cols-2 gap-2', className)}>
      {/* Clockwise: the DOM order is row-major, so the bottom pair is swapped
          to make the lit square travel round rather than zig-zag. */}
      {['aw-square-1', 'aw-square-2', 'aw-square-4', 'aw-square-3'].map((step) => (
        <span key={step} className={cx('aw-square size-3.5 rounded-[4px] bg-ink', step)} />
      ))}
    </span>
  );
}

/**
 * `full` is for the places this stands in for the whole page rather than for
 * one screen's content: the session gate renders outside `AppLayout`, with no
 * header or nav around it, so `40vh` left the animation hanging a fifth of
 * the way down an otherwise empty window instead of in the middle of it.
 *
 * `dvh` rather than `vh` because the target is a phone, where `vh` counts the
 * browser chrome that is not actually there.
 */
export function ScreenSkeleton({ full = false }: { full?: boolean }) {
  return (
    <div
      className={cx(
        'flex flex-col items-center justify-center gap-4 p-4',
        // `full` is the session gate, which renders before the shell exists
        // and so has to measure itself against the viewport. Inside the shell
        // it is a grid item instead, stretched to whatever `main` has left,
        // and `min-h-[40vh]` is only a floor for the rare case where that
        // space is tiny.
        full ? 'min-h-dvh' : 'h-full min-h-[40vh]',
      )}
      aria-busy="true"
      aria-live="polite"
    >
      <PulseSquares />
      <span className="sr-only">Loading</span>
    </div>
  );
}

/**
 * A list that could not be loaded.
 *
 * Every list screen used to render its *empty* state when the request failed:
 * `posts.data ?? []` is `[]` either way, so a 500 and a genuinely empty
 * account were indistinguishable. In an app whose entire job is catching posts
 * that did not land, "Nothing here" is the one answer that must never be a
 * guess — an owner seeing it would stop looking.
 *
 * Retry rather than a reload, because a reload loses the tab, the filter and
 * the scroll position the owner was in the middle of using.
 */
export function ScreenError({
  message,
  onRetry,
  busy,
}: {
  message: string;
  onRetry: () => void;
  busy?: boolean;
}) {
  return (
    <div role="alert" className="rounded-aw border border-bad/40 bg-bad-soft px-5 py-8 text-center">
      <p className="flex items-center justify-center gap-1.5 font-display text-lg text-bad">
        <span aria-hidden="true">✕</span>
        Could not load this
      </p>
      <p className="mx-auto mt-1 max-w-sm text-sm leading-relaxed text-ink">{message}</p>
      <div className="mt-4 flex justify-center">
        <Button onClick={onRetry} {...(busy === undefined ? {} : { busy })}>
          Try again
        </Button>
      </div>
    </div>
  );
}

/** A consistent page heading, so every screen starts the same way. */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string | undefined;
  action?: React.ReactNode | undefined;
}) {
  return (
    <div className="flex items-start justify-between gap-3 pt-4 pb-3">
      <div className="min-w-0">
        <h1 className="font-display text-xl leading-tight font-normal tracking-[-0.01em] text-ink">
          {title}
        </h1>
        {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
